import { CardHeader } from '@/components/card-header';
import {
  ChargeStatusColorMap,
  ChargeStatusLabelMap,
  ChargeStatuses,
} from '@/constants/charge-status';
import { SquareRounded } from '@/constants/hardfin-standard-icons';
import { Project } from '@/project/api/types';
import { ProjectBillingChart } from '@/project/components/project-billing-chart';
import { deriveProjectCurrency } from '@/project/utilities/project-currency';
import {
  Box,
  Divider,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { DateTime } from 'luxon';

/**
 * Props for the ProjectBillingCard component
 */
interface Props {
  /**
   * A collection of projects to display billing information for
   */
  projects: Project[];
}

/**
 * A card component that displays project billing information using a chart
 */
export function ProjectBillingCard({ projects }: Props) {
  const theme = useTheme();
  const isSingleProject = projects.length === 1;

  return (
    <Paper
      variant="outlined"
      sx={{ display: 'flex', flexDirection: 'column', mb: 3 }}
    >
      <CardHeader
        title={
          isSingleProject
            ? 'Billing status for this project'
            : 'Projects billing summary'
        }
        variant={isSingleProject ? 'subtitle2' : 'subtitle1'}
      />
      <Divider />
      <Box padding={2}>
        <ProjectBillingChart
          projects={projects}
          currencyCode={
            isSingleProject
              ? (deriveProjectCurrency([
                  projects[0].totalContractValue,
                  projects[0].discount,
                ]) ?? undefined)
              : undefined
          }
        />
      </Box>
      <Divider />
      <Box padding={2}>
        <Stack spacing={2}>
          <Typography variant="subtitle2">Legend</Typography>
          <Stack direction="row" flexWrap="wrap" gap={3}>
            {ChargeStatuses.map((status) => (
              <Stack
                key={status}
                direction="row"
                alignItems="center"
                spacing={0.5}
              >
                <SquareRounded
                  fontSize="small"
                  sx={{ fill: ChargeStatusColorMap(status, theme) }}
                />
                <Typography variant="body2">
                  {ChargeStatusLabelMap(status)}
                </Typography>
              </Stack>
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {/* TODO(#15942): Pull last update time from billing periods */}
            Last update reported on {DateTime.now().toFormat('MMMM d, yyyy')}
          </Typography>
        </Stack>
      </Box>
    </Paper>
  );
}
